import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormModelControl } from '@bcl/common-ui';
import { SelectOption } from '@bcl/design-system';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StateService } from 'src/app/services/state.service';
import { StorageService } from 'src/app/services/storage.service';

type TimesheetForm = {
  timesheetTypeId: string | null;
  warehouseId: string | null;
  timesheetCarMileage: number | null;
};
@Component({
  selector: 'app-timesheet-dialog',
  templateUrl: './timesheet-dialog.component.html',
  styleUrls: ['./timesheet-dialog.component.scss'],
})
export class TimesheetDialogComponent implements OnInit, OnDestroy {
  public timesheetForm: FormGroup<FormModelControl<TimesheetForm>>;
  public timesheetTypeOptions: SelectOption[] = [];
  public warehouseOptions: SelectOption[] = [];
  public actionType: string;
  public isPaused = false;
  private destroy$ = new Subject<void>();
  constructor(
    public dialogRef: MatDialogRef<TimesheetDialogComponent>,
    fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any | null,
    private stateService: StateService,
    private storageService: StorageService
  ) {
    this.timesheetForm = fb.group({
      timesheetTypeId: [null as string | null, Validators.required],
      warehouseId: [null as string | null],
      timesheetCarMileage: [null as number | null],
    });
    if (data) {
      this.actionType = data.actionType;
      this.isPaused = data.isPaused;
      this.warehouseOptions = data.warehouseOptions || [];
      this.timesheetTypeOptions = data.timesheetTypeOptions.filter(
        (type) => !['1', '2', '4'].includes(type.value)
      );

      if (this.actionType === 'start_day') {
        this.timesheetForm
          .get('timesheetCarMileage')
          ?.setValidators(Validators.required);
        this.timesheetForm
          .get('warehouseId')
          ?.setValidators(Validators.required);
        this.timesheetForm.get('timesheetTypeId')?.setValue('1');
      }
      if (this.actionType === 'end_day') {
        this.timesheetForm
          .get('timesheetCarMileage')
          ?.setValidators(Validators.required);
        this.timesheetForm.get('timesheetTypeId')?.setValue('2');
      }
    }
  }

  ngOnInit(): void {
    this.loadSelectedCarForToday();
    this.setupCarChangeListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async save(): Promise<void> {
    const formValue = this.timesheetForm.value;

    if (
      (this.actionType === 'start_day' || this.actionType === 'end_day') &&
      formValue.warehouseId
    ) {
      await this.saveSelectedCarToStorage(formValue.warehouseId);
    }

    if (formValue.warehouseId && formValue.timesheetCarMileage) {
      this.updateCarMileageInWarehouseOptions(
        formValue.warehouseId,
        formValue.timesheetCarMileage
      );
    }

    this.dialogRef.close({
      timesheetTypeId: formValue.timesheetTypeId,
      warehouseId: formValue.warehouseId,
      timesheetCarMileage: formValue.timesheetCarMileage,
    });
  }

  stopPause(): void {
    this.dialogRef.close({
      timesheetTypeId: 999,
    });
  }

  private async saveSelectedCarToStorage(selectedCarId: string): Promise<void> {
    try {
      await this.cleanupOldSelectedCarEntries();

      const currentDate = new Date();
      const dateKey = currentDate.toISOString().split('T')[0]; // Gets YYYY-MM-DD format
      const storageKey = `${dateKey}_selectedCar`;

      await this.storageService.store(storageKey, selectedCarId);
    } catch (error) {
      console.error('Error saving selected car to storage:', error);
    }
  }

  private async cleanupOldSelectedCarEntries(): Promise<void> {
    try {
      const storage = await this.storageService.getStorage();
      const keys = await storage.keys();

      const selectedCarKeys = keys.filter((key) =>
        key.endsWith('_selectedCar')
      );

      // Remove all old selectedCar entries
      for (const key of selectedCarKeys) {
        await this.storageService.removeStorageItem(key);
      }
    } catch (error) {
      console.error('Error cleaning up old selected car entries:', error);
    }
  }

  private async loadSelectedCarForToday(): Promise<void> {
    try {
      const currentDate = new Date();
      const dateKey = currentDate.toISOString().split('T')[0];
      const storageKey = `${dateKey}_selectedCar`;

      // Try to get today's selected car
      const todaysSelectedCar = await this.storageService.getObject(storageKey);

      if (todaysSelectedCar && this.warehouseOptions.length > 0) {
        const carExists = this.warehouseOptions.some(
          (option) => option.value === todaysSelectedCar
        );
        if (carExists) {
          this.timesheetForm.get('warehouseId')?.setValue(todaysSelectedCar);
          this.setCarMileageFromWarehouseOptions(todaysSelectedCar);
          return;
        }
      }

      const defaultCar = this.stateService.getValue('defaultCar');
      if (defaultCar && this.warehouseOptions.length > 0) {
        this.timesheetForm.get('warehouseId')?.setValue(defaultCar.toString());
        this.setCarMileageFromWarehouseOptions(defaultCar.toString());
      }
    } catch (error) {
      const defaultCar = this.stateService.getValue('defaultCar');
      if (defaultCar && this.warehouseOptions.length > 0) {
        this.timesheetForm.get('warehouseId')?.setValue(defaultCar.toString());
        this.setCarMileageFromWarehouseOptions(defaultCar.toString());
      }
    }
  }

  private setCarMileageFromWarehouseOptions(carId: string): void {
    try {
      const selectedWarehouse = this.warehouseOptions.find(
        (option) => option.value === carId
      );

      if (selectedWarehouse) {
        const carMileage = (selectedWarehouse as any).carMileage;

        if (carMileage && typeof carMileage === 'number' && carMileage > 0) {
          this.timesheetForm.get('timesheetCarMileage')?.setValue(carMileage);
        } else {
          // Clear the field if mileage is 0, null, or undefined
          this.timesheetForm.get('timesheetCarMileage')?.setValue(null);
        }
      }
    } catch (error) {
      console.error('Error setting car mileage from warehouse options:', error);
    }
  }

  private updateCarMileageInWarehouseOptions(
    carId: string,
    newMileage: number
  ): void {
    try {
      const carIndex = this.warehouseOptions.findIndex(
        (option) => option.value === carId
      );
      if (carIndex !== -1) {
        (this.warehouseOptions[carIndex] as any).carMileage = newMileage;
      }
    } catch (error) {
      console.error('Error updating car mileage in warehouse options:', error);
    }
  }

  private setupCarChangeListener(): void {
    if (this.actionType === 'start_day') {
      this.timesheetForm
        .get('warehouseId')
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((carId) => {
          if (carId) {
            this.setCarMileageFromWarehouseOptions(carId);
          } else {
            // Clear car mileage when car ID is cleared
            this.timesheetForm.get('timesheetCarMileage')?.setValue(null);
          }
        });
    }
  }
}
